import type { Components, Theme } from '@mui/material/styles';
import { navy, solar } from './palette';

/**
 * Component overrides. Restrained on purpose: soft radii, hairline borders,
 * shadows only where elevation carries meaning.
 */
export const components = (mode: 'light' | 'dark'): Components<Theme> => {
  const dark = mode === 'dark';
  const line = dark ? 'rgba(163,190,219,0.16)' : navy[200];

  return {
    MuiCssBaseline: {
      styleOverrides: {
        html: { WebkitFontSmoothing: 'antialiased', scrollBehavior: 'smooth' },
        body: { overflowX: 'hidden' },
        '::selection': {
          background: solar[300],
          color: navy[900],
        },
        '@media (prefers-reduced-motion: reduce)': {
          '*': { animationDuration: '0.01ms !important', transitionDuration: '0.01ms !important' },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 10, paddingInline: 20, paddingBlock: 10 },
        sizeLarge: { paddingInline: 28, paddingBlock: 14, fontSize: '1rem' },
        outlined: { borderColor: dark ? navy[600] : navy[300] },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: line },
      },
    },
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          borderRadius: 14,
          borderColor: line,
          transition: 'border-color .2s ease, transform .2s ease',
        },
      },
    },
    MuiTextField: { defaultProps: { variant: 'outlined', size: 'medium' } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 10, backgroundColor: dark ? navy[950] : navy[50] },
        notchedOutline: { borderColor: line },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 7, fontWeight: 600, fontSize: '0.75rem' },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: line },
        head: {
          fontWeight: 700,
          fontSize: '0.75rem',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: dark ? navy[300] : navy[600],
          backgroundColor: dark ? navy[950] : navy[100],
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:last-child td': { borderBottom: 0 } },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: { root: { borderBottom: `1px solid ${line}`, backgroundImage: 'none' } },
    },
    MuiDrawer: { styleOverrides: { paper: { borderColor: line, backgroundImage: 'none' } } },
    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: 9, marginInline: 8, '&.Mui-selected': { fontWeight: 600 } },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: { tooltip: { borderRadius: 7, fontSize: '0.75rem' } },
    },
    MuiAlert: { styleOverrides: { root: { borderRadius: 10 } } },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 16, border: `1px solid ${line}` } } },
    MuiLink: { defaultProps: { underline: 'hover' } },
    MuiDivider: { styleOverrides: { root: { borderColor: line } } },
  };
};
